import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { isNil } from 'lodash';

import { CommunicationWorkflowTargetOrderByName } from '@pwp-common';

import { FormGroupControlValueAccessor } from '../../../../../generic/abstract-classes/form-group-control-value-accessor';
import { WorkerEditorOutputItem } from '../../../../workers-autocomplete/editor-output/core/interface';
import { formToTargetOrderByEditorOutput } from '../../editor-output/target-order-by/form-to-target-order-by-editor-output/form-to-target-order-by-editor-output';
import { TargetOrderByEditorOutput } from '../../editor-output/target-order-by/target-order-by-editor-output';
import { TargetOrderByFormType } from '../../editor-output/target-order-by/target-order-by-form-type';
import { targetOrderByToFormType } from '../../editor-output/target-order-by/target-order-by-to-form-type/target-order-by-to-form-type';

const VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TargetOrderByEditorComponent),
  multi: true,
};

const VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => TargetOrderByEditorComponent),
  multi: true,
};

@UntilDestroy()
@Component({
  selector: 'app-target-order-by-editor',
  templateUrl: './target-order-by-editor.component.html',
  styleUrls: ['./target-order-by-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VALUE_ACCESSOR, VALIDATOR],
})
export class TargetOrderByEditorComponent
  extends FormGroupControlValueAccessor<TargetOrderByFormType, TargetOrderByEditorOutput>
  implements OnChanges
{
  /////////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////////////////////

  @Input() public workers: WorkerEditorOutputItem[] = [];

  public readonly OrderByType = CommunicationWorkflowTargetOrderByName;

  public orderByOptions = [
    {
      label: 'specifiedOrderLabel',
      value: CommunicationWorkflowTargetOrderByName.explicit,
    },
    {
      label: 'longestIdleTimeLabel',
      value: CommunicationWorkflowTargetOrderByName.longestIdleTime,
    },
  ];

  public get hasEventWorker(): boolean {
    return Array.isArray(this.workers) && this.workers.some((worker) => worker.type === 'event');
  }

  constructor(public changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.workers) {
      this.updateUI();
    }
  }

  private updateUI() {
    if (this.hasEventWorker) {
      this.type.setValue(CommunicationWorkflowTargetOrderByName.explicit);
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Define Form
  /////////////////////////////////////////////////////////////////////////////////////////////
  public defineForm() {
    this.form = new UntypedFormGroup({
      type: new UntypedFormControl(CommunicationWorkflowTargetOrderByName.explicit, [Validators.required]),
    });
    this.updateUI();
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Write Value
  /////////////////////////////////////////////////////////////////////////////////////////////
  public writeValue(value: TargetOrderByEditorOutput) {
    if (isNil(value)) return;

    const formType = targetOrderByToFormType(value);
    if (this.hasEventWorker) {
      formType.type = CommunicationWorkflowTargetOrderByName.explicit;
    }
    super.writeValue(formType);
    this.updateUI();
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Parse Value Change
  /////////////////////////////////////////////////////////////////////////////////////////////
  public parseValueChange(value: TargetOrderByFormType): TargetOrderByEditorOutput {
    if (this.hasEventWorker) {
      return formToTargetOrderByEditorOutput({
        type: CommunicationWorkflowTargetOrderByName.explicit,
      });
    }
    return formToTargetOrderByEditorOutput(value);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Validation Errors
  /////////////////////////////////////////////////////////////////////////////////////////////
  protected makeValidationErrors() {
    return {
      'target-order-by-editor': this.form.value,
    };
  }

  ///////////////////////////////////////////////////////////////////////////////////////////
  // Form Controls
  ///////////////////////////////////////////////////////////////////////////////////////////
  public get type() {
    return this.form.get('type') as UntypedFormControl;
  }
}
